import { FaX } from 'react-icons/fa6';
import { QueueTypeResponse, QueueSubmissionProcessingResult } from '~/types/fundrequests';
import { setFundRequestStatus, submitQueue } from '~/services/fund-requests';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { FundRequestStatus } from '~/enums/fundRequest';
import ConfirmationModal from './ConfirmationModal';
import ProcessingResultsModal from './ProcessingResultsModal';
import QueueTestControls from './QueueTestControls';
import { Link } from 'react-router-dom';

export const QueueTable = ({
    queue,
    paymentType,
    refetch,
}: {
    queue: QueueTypeResponse[] | undefined;
    paymentType: string;
    refetch: () => void;
}) => {
    const [submitting, setSubmitting] = useState(false);
    const itemCount = queue?.length;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [processingResult, setProcessingResult] = useState<QueueSubmissionProcessingResult | null>(null);
    const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
    const [resultMessage, setResultMessage] = useState<string>('');
    const [resultError, setResultError] = useState<string>('');
    const [isResultError, setIsResultError] = useState(false);

    const confirmationModal = () => {
        setIsModalOpen(true);
    };

    const handleSubmit = async (scheduledDate?: string) => {
        try {
            setSubmitting(true);
            const resp = await submitQueue(paymentType, scheduledDate);
            console.log('resp: ', resp);

            // Always refetch the queue data first
            refetch();

            // Handle the new response structure
            if (resp.processing_result) {
                setProcessingResult(resp.processing_result);
                setResultMessage(resp.message || '');
                setResultError(resp.error || '');
                setIsResultError(!!resp.error);

                // Show appropriate toast based on result
                const { successful_count, failed_count, total_requests } = resp.processing_result;

                if (failed_count === 0) {
                    // All successful
                    toast.success(`All ${successful_count} fund requests processed successfully`);
                } else if (successful_count === 0) {
                    // All failed
                    toast.error(`All ${failed_count} fund requests failed to process`);
                } else {
                    // Partial success
                    toast.success(`${successful_count} of ${total_requests} fund requests processed successfully`);
                }

                // Show the results modal
                setIsResultsModalOpen(true);
            } else {
                // Fallback for old response format
                toast.success('Queue submitted successfully');
            }

            setSubmitting(false);
            setIsModalOpen(false);
            return resp;
        } catch (e: any) {
            // Handle error cases
            setSubmitting(false);

            // Check if error response has processing_result
            if (e.response?.data?.processing_result) {
                setProcessingResult(e.response.data.processing_result);
                setResultMessage(e.response.data.message || '');
                setResultError(e.response.data.error || 'Processing failed');
                setIsResultError(true);
                setIsResultsModalOpen(true);

                // Show appropriate toast
                const { failed_count } = e.response.data.processing_result;
                toast.error(`${failed_count} fund requests failed to process`);
            } else {
                // Fallback error handling
                toast.error('Error submitting queue');
            }

            console.log(e);
            throw e;
        }
    };

    const handleRemove = async (id: number) => {
        try {
            setSubmitting(true);
            toast.loading(`Removing Fund Request ${id} from the Queue...`);
            const resp = await setFundRequestStatus(
                id.toString(),
                FundRequestStatus.AP_REVIEW,
                null
            );
            console.log('resp: ', resp);
            refetch();
            toast.dismiss();
            toast.success(`Fund Request ${id} removed from Queue`);
            setSubmitting(false);
        } catch (e) {
            toast.dismiss();
            toast.error('Error removing from queue');
            setSubmitting(false);
            console.log(e);
        }
    };

    const handleCloseResultsModal = () => {
        setIsResultsModalOpen(false);
        setProcessingResult(null);
        setResultMessage('');
        setResultError('');
        setIsResultError(false);
    };

    const submitText = submitting ? 'Submitting...' : `Submit `;

    return (
        <div className='p-3'>
            <div className='overflow-x-auto'>
                <table className='datatable-table datatable-one w-full table-auto border-collapse overflow-hidden px-4 md:table-fixed md:px-8'>
                    <thead className='border-separate px-4'>
                        <tr
                            className='border-t border-stroke bg-whiten'
                            style={{ fontSize: '14px' }}
                        >
                            <th
                                style={{
                                    paddingTop: '6px',
                                    paddingBottom: '6px',
                                    width: '60px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                ID
                            </th>
                            <th
                                style={{
                                    paddingTop: '6px',
                                    paddingBottom: '6px',
                                }}
                            >
                                Invoice Number
                            </th>
                            <th
                                style={{
                                    paddingTop: '6px',
                                    paddingBottom: '6px',
                                }}
                            >
                                Payee
                            </th>
                            <th
                                style={{
                                    width: '60px',
                                    paddingTop: '6px',
                                    paddingBottom: '6px',
                                }}
                            ></th>
                        </tr>
                    </thead>
                    <tbody>
                        {queue?.map((item, index) => (
                            <tr key={index}>
                                <td
                                    className="align-middle"
                                    style={{
                                        paddingTop: '6px',
                                        paddingBottom: '6px',
                                        width: '60px',
                                        whiteSpace: 'nowrap',
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    <Link
                                        to={`/fund-requests/${item.id}`}
                                        className="font-semibold text-primary hover:bg-primary/10 active:bg-primary/20 transition-all duration-200 active:scale-95 rounded px-2 py-1 inline-block"
                                        title={`View Fund Request #${item.id}`}
                                    >
                                        {item.id}
                                    </Link>
                                </td>
                                <td
                                    className="align-middle"
                                    style={{
                                        paddingTop: '6px',
                                        paddingBottom: '6px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '150px',
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    {item.ap_invoice_number}
                                </td>
                                <td
                                    className="align-middle"
                                    style={{
                                        paddingTop: '6px',
                                        paddingBottom: '6px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '200px',
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    {item.payee}
                                </td>
                                <td
                                    className="align-middle"
                                    style={{
                                        paddingTop: '6px',
                                        paddingBottom: '6px',
                                        width: '60px',
                                        textAlign: 'center',
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    <button
                                        onClick={() => handleRemove(item.id)}
                                        disabled={submitting}
                                        className="px-3 py-1"
                                    >
                                        <FaX className='text-danger' size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <button
                className='mt-3 rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                onClick={confirmationModal}
                disabled={submitting}
            >
                Submit ({itemCount})
            </button>

            {/* Test Controls - only visible in development */}
            <QueueTestControls paymentType={paymentType} refetch={refetch} />

            <ConfirmationModal
                submitText={submitText}
                submitting={submitting}
                handleSubmit={handleSubmit}
                handleRemove={handleRemove}
                queue={queue}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
            {processingResult && (
                <ProcessingResultsModal
                    isOpen={isResultsModalOpen}
                    onClose={handleCloseResultsModal}
                    processingResult={processingResult}
                    isError={isResultError}
                    message={resultMessage}
                    error={resultError}
                />
            )}
        </div>
    );
};
